import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../../../auth/useAuth";
import { ADMIN_PERMISSIONS } from "../../../../auth/adminPermissions";
import { fetchVehicleSources } from "../../../../services/vehicleSourceApi";
import {
  fetchVehicleDataQualityIssues,
  patchVehicleDataQualityIssue,
  recalculateAllVehicleDataQuality,
} from "../../../../services/vehicleDataQualityApi";
import AdminStatePanel from "../layout/AdminStatePanel";
import AdminTableContainer from "../layout/AdminTableContainer";
import RowActionMenu from "../layout/RowActionMenu";
import "./vehicleDataQualityPage.css";

const SEVERITY_OPTIONS = [
  { value: "CRITICAL", label: "Critical" },
  { value: "HIGH", label: "High" },
  { value: "MEDIUM", label: "Medium" },
  { value: "LOW", label: "Low" },
  { value: "INFO", label: "Info" },
];

const CATEGORY_OPTIONS = [
  { value: "COMPLETENESS", label: "Missing details" },
  { value: "PRICE", label: "Price" },
  { value: "MILEAGE", label: "Mileage" },
  { value: "YEAR", label: "Year" },
  { value: "NORMALIZATION", label: "Name or model" },
  { value: "PARSER", label: "Parser" },
  { value: "LIFECYCLE", label: "Listing status" },
  { value: "DUPLICATE", label: "Possible duplicate" },
  { value: "CANONICAL", label: "Vehicle grouping" },
  { value: "LOCATION", label: "Location" },
  { value: "IMAGE", label: "Photos" },
];

const STATUS_OPTIONS = [
  { value: "OPEN", label: "Open" },
  { value: "IN_REVIEW", label: "In review" },
  { value: "RESOLVED", label: "Resolved" },
  { value: "IGNORED", label: "Ignored" },
  { value: "FALSE_POSITIVE", label: "Not a real problem" },
  { value: "NEEDS_PARSER_FIX", label: "Needs a parser fix" },
  { value: "NEEDS_NORMALIZATION_FIX", label: "Needs a name fix" },
];

const SORT_OPTIONS = [
  { value: "severity", label: "Most serious first" },
  { value: "detected_at", label: "Newest first" },
  { value: "issue_category", label: "Problem type" },
  { value: "source", label: "Source name" },
  { value: "quality_score", label: "Listing score" },
];

function formatCount(value) {
  return Number(value || 0).toLocaleString();
}

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function labelFor(options, value) {
  return options.find((option) => option.value === value)?.label || value || "—";
}

function severityClass(severity) {
  if (severity === "CRITICAL") return "admin-badge--danger";
  if (severity === "HIGH") return "admin-badge--warn";
  return "admin-badge--muted";
}

function vehicleLabel(issue) {
  const parts = [issue.makeNormalized, issue.modelNormalized, issue.year].filter(Boolean);
  return parts.length ? parts.join(" ") : issue.vehicleListingId?.slice(0, 8) || "—";
}

function unscoredCount(overview) {
  if (!overview) return 0;
  if (overview.unscoredListings != null) return overview.unscoredListings;
  const scored =
    (overview.excellentQuality || 0) +
    (overview.goodQuality || 0) +
    (overview.usableQuality || 0) +
    (overview.needsReview || 0) +
    (overview.poorQuality || 0);
  return Math.max(0, (overview.totalListings || 0) - scored);
}

function openReviewCount(overview) {
  if (!overview) return 0;
  if (overview.openReviewItems != null) return overview.openReviewItems;
  return (
    (overview.openCriticalIssues || 0) +
    (overview.openHighIssues || 0) +
    (overview.openMediumIssues || 0)
  );
}

const VehicleDataQualityPage = ({
  onClose,
  onOpenListings,
  onOpenSourceHealth,
  onOpenNormalization,
  onOpenDuplicates,
  onOpenIngestionRuns,
  initialSourceId,
}) => {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage =
    isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);
  const hasLoadedRef = useRef(false);

  const [issues, setIssues] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [scoring, setScoring] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [toast, setToast] = useState("");

  const [severity, setSeverity] = useState("");
  const [issueCategory, setIssueCategory] = useState("");
  const [status, setStatus] = useState("");
  const [vehicleSourceId, setVehicleSourceId] = useState(initialSourceId || "");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("severity");
  const [sources, setSources] = useState([]);
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [selectedIssueId, setSelectedIssueId] = useState(null);

  const filtersActive = Boolean(severity || issueCategory || status || vehicleSourceId || search);

  const load = useCallback(async () => {
    try {
      if (hasLoadedRef.current) setRefreshing(true);
      else setLoading(true);
      setErrorMessage("");
      const data = await fetchVehicleDataQualityIssues({
        severity: severity || undefined,
        issueCategory: issueCategory || undefined,
        status: status || undefined,
        vehicleSourceId: vehicleSourceId || undefined,
        search: search.trim() || undefined,
        sort,
        sortDir: "desc",
        openOnly: status ? undefined : "true",
        limit: 200,
      });
      setIssues(data.issues || []);
      setOverview(data.overview || null);
      hasLoadedRef.current = true;
    } catch (error) {
      setIssues([]);
      setErrorMessage(error.message || "Unable to load data quality.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [severity, issueCategory, status, vehicleSourceId, search, sort]);

  useEffect(() => {
    fetchVehicleSources({ sort: "source_name", sortDir: "asc" })
      .then((data) => setSources(data.sources || []))
      .catch(() => setSources([]));
  }, []);

  useEffect(() => {
    if (initialSourceId) setVehicleSourceId(initialSourceId);
  }, [initialSourceId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRecalculateAll = async () => {
    if (!canManage || scoring) return;
    try {
      setScoring(true);
      const result = await recalculateAllVehicleDataQuality({ maxListings: 100 });
      setToast(
        `Checked ${formatCount(result.processed)} listing${result.processed === 1 ? "" : "s"}. New problems appear in the review queue.`
      );
      await load();
    } catch (error) {
      setToast(error.message || "Scoring failed.");
    } finally {
      setScoring(false);
    }
  };

  const handlePatch = async (id, patch) => {
    if (!canManage) return;
    try {
      await patchVehicleDataQualityIssue(id, {
        ...patch,
        resolutionNotes: resolutionNotes.trim() || patch.resolutionNotes,
      });
      setToast(`Marked ${labelFor(STATUS_OPTIONS, patch.status).toLowerCase()}.`);
      setResolutionNotes("");
      setSelectedIssueId(null);
      await load();
    } catch (error) {
      setToast(error.message || "Update failed.");
    }
  };

  const clearFilters = () => {
    setSeverity("");
    setIssueCategory("");
    setStatus("");
    setVehicleSourceId("");
    setSearchInput("");
    setSearch("");
    setSort("severity");
  };

  const unscored = unscoredCount(overview);
  const openReview = openReviewCount(overview);
  const scored = Math.max(0, (overview?.totalListings || 0) - unscored);
  const normalizationOpen = overview?.openNormalizationIssues || 0;
  const duplicateOpen = overview?.openDuplicateReviewIssues || 0;

  const emptyCopy = filtersActive
    ? "Nothing in the review queue matches these filters."
    : unscored > 0
      ? `The review queue is empty because ${formatCount(unscored)} listings have not been checked yet. Score a batch to find missing prices, mileage, years, and other listing problems.`
      : "The review queue is empty. Checked listings do not have open problems.";

  return (
    <div className="admin-dashboard admin-vehicle-data-quality-dash admin-vehicle-sources-dash--full-width">
      <header className="admin-dashboard__header">
        <div>
          <p className="admin-dashboard__eyebrow">Vehicle Market</p>
          <h1 className="admin-dashboard__title">Data Quality</h1>
          <p className="admin-dashboard__subtitle">
            See which listings still need a quality check, which fields buyers are missing,
            and which problems are waiting for a person to review.
          </p>
        </div>
        <div className="admin-dashboard__header-actions">
          <button type="button" className="admin-dashboard__action-btn" onClick={load}>
            Refresh
          </button>
          {canManage ? (
            <button
              type="button"
              className="admin-dashboard__action-btn"
              onClick={handleRecalculateAll}
              disabled={scoring}
            >
              {scoring ? "Scoring listings…" : "Score next 100 listings"}
            </button>
          ) : null}
          {onClose ? (
            <button type="button" className="admin-dashboard__action-btn" onClick={onClose}>
              Close
            </button>
          ) : null}
        </div>
      </header>

      {toast ? (
        <p className="admin-dashboard__toast" role="status">
          {toast}
        </p>
      ) : null}

      {loading && !overview ? (
        <AdminStatePanel message="Loading data quality…" />
      ) : errorMessage && !overview ? (
        <AdminStatePanel message={errorMessage} variant="error" />
      ) : overview ? (
        <>
          <section className="vdq-grid" aria-label="What to do next">
            <article className={`vdq-card${unscored > 0 ? " vdq-card--attention" : " vdq-card--quiet"}`}>
              <span className="vdq-kicker">Not checked yet</span>
              <span className="vdq-value">{formatCount(unscored)}</span>
              <p>
                Listings with no quality score. Scoring reads each listing and adds real problems
                to the review queue below.
              </p>
              {canManage ? (
                <button
                  type="button"
                  className="admin-dashboard__action-btn"
                  onClick={handleRecalculateAll}
                  disabled={scoring}
                >
                  {scoring ? "Scoring listings…" : "Score next 100"}
                </button>
              ) : null}
            </article>
            <article className="vdq-card">
              <span className="vdq-kicker">Waiting in this queue</span>
              <span className="vdq-value">{formatCount(openReview)}</span>
              <p>
                {openReview > 0
                  ? `${formatCount(overview.openCriticalIssues)} critical, ${formatCount(overview.openHighIssues)} high, and ${formatCount(overview.openMediumIssues)} medium.`
                  : "Nothing is waiting for review. Score unchecked listings if you expect missing prices, mileage, or years to appear here."}
              </p>
            </article>
            <article className={`vdq-card${normalizationOpen > 0 ? " vdq-card--attention" : ""}`}>
              <span className="vdq-kicker">Names still unmatched</span>
              <span className="vdq-value">{formatCount(normalizationOpen)}</span>
              <p>
                These live on the normalization page, not in the list below. They are make, model,
                and trim names that did not match the catalog.
              </p>
              {onOpenNormalization ? (
                <button
                  type="button"
                  className="admin-dashboard__action-btn"
                  onClick={onOpenNormalization}
                >
                  Open normalization
                </button>
              ) : null}
            </article>
          </section>

          <section className="vdq-section">
            <div>
              <h2>Active listings missing a field</h2>
              <p>
                Counts from live inventory. A missing field becomes a review item only after that
                listing is scored.
              </p>
            </div>
            <div className="vdq-metrics">
              <div className="vdq-metric">
                <strong>{formatCount(overview.listingsMissingPrice)}</strong>
                <span>Missing price</span>
              </div>
              <div className="vdq-metric">
                <strong>{formatCount(overview.listingsMissingMileage)}</strong>
                <span>Missing mileage</span>
              </div>
              <div className="vdq-metric">
                <strong>{formatCount(overview.listingsMissingYear)}</strong>
                <span>Missing year</span>
              </div>
              <div className="vdq-metric">
                <strong>{formatCount(overview.totalListings)}</strong>
                <span>Listings in inventory</span>
              </div>
            </div>
          </section>

          <section className="vdq-section">
            <div>
              <h2>Scores on checked listings</h2>
              <p>
                {formatCount(scored)} of {formatCount(overview.totalListings)} listings have been
                scored. Excellent is 90 or above. Poor is under 40.
              </p>
            </div>
            <div className="vdq-metrics">
              <div className="vdq-metric">
                <strong>{formatCount(overview.excellentQuality)}</strong>
                <span>Excellent</span>
              </div>
              <div className="vdq-metric">
                <strong>{formatCount(overview.goodQuality)}</strong>
                <span>Good</span>
              </div>
              <div className="vdq-metric">
                <strong>{formatCount(overview.usableQuality)}</strong>
                <span>Usable</span>
              </div>
              <div className="vdq-metric">
                <strong>{formatCount(overview.needsReview)}</strong>
                <span>Needs review</span>
              </div>
              <div className="vdq-metric">
                <strong>{formatCount(overview.poorQuality)}</strong>
                <span>Poor</span>
              </div>
            </div>
          </section>

          <section className="vdq-section">
            <div>
              <h2>Other vehicle queues</h2>
              <p>Separate lists. Their counts are not rows in the review queue on this page.</p>
            </div>
            <div className="vdq-links">
              {onOpenDuplicates ? (
                <button type="button" className="vdq-link-card" onClick={onOpenDuplicates}>
                  <span className="vdq-kicker">Duplicate review</span>
                  <strong>{formatCount(duplicateOpen)} open</strong>
                  <p>Possible matches for the same vehicle across sources.</p>
                </button>
              ) : null}
              {onOpenSourceHealth ? (
                <button type="button" className="vdq-link-card" onClick={onOpenSourceHealth}>
                  <span className="vdq-kicker">Source health</span>
                  <strong>By dealer source</strong>
                  <p>Freshness, failures, and quality for each vehicle source.</p>
                </button>
              ) : null}
              {onOpenIngestionRuns ? (
                <button type="button" className="vdq-link-card" onClick={onOpenIngestionRuns}>
                  <span className="vdq-kicker">Ingestion</span>
                  <strong>Recent runs</strong>
                  <p>Whether the latest source pulls completed or failed.</p>
                </button>
              ) : null}
            </div>
          </section>

          <section className="vdq-section">
            <div className="vdq-toolbar">
              <div>
                <h2>Review queue</h2>
                <p>
                  Problems found on individual listings. The default view is everything still open.
                </p>
              </div>
              {refreshing ? <span className="vdq-updating">Updating…</span> : null}
            </div>

            <form
              className="vdq-filters"
              onSubmit={(event) => {
                event.preventDefault();
                setSearch(searchInput.trim());
              }}
            >
              <label>
                Search
                <input
                  type="search"
                  placeholder="Summary, problem, or make"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                />
              </label>
              <label>
                Severity
                <select value={severity} onChange={(event) => setSeverity(event.target.value)}>
                  <option value="">Any severity</option>
                  {SEVERITY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Problem
                <select
                  value={issueCategory}
                  onChange={(event) => setIssueCategory(event.target.value)}
                >
                  <option value="">Any problem</option>
                  {CATEGORY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Status
                <select value={status} onChange={(event) => setStatus(event.target.value)}>
                  <option value="">Open and in progress</option>
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Source
                <select
                  value={vehicleSourceId}
                  onChange={(event) => setVehicleSourceId(event.target.value)}
                >
                  <option value="">All sources</option>
                  {sources.map((source) => (
                    <option key={source.id} value={source.id}>
                      {source.sourceName}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Sort
                <select value={sort} onChange={(event) => setSort(event.target.value)}>
                  {SORT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <button type="submit" className="admin-dashboard__action-btn">
                Search
              </button>
              {filtersActive ? (
                <button type="button" className="admin-dashboard__action-btn" onClick={clearFilters}>
                  Clear filters
                </button>
              ) : null}
            </form>

            {errorMessage ? (
              <AdminStatePanel message={errorMessage} variant="error" />
            ) : !issues.length ? (
              <AdminStatePanel message={emptyCopy} />
            ) : (
              <AdminTableContainer>
                <table className="admin-data-table">
                  <thead>
                    <tr>
                      <th>Problem</th>
                      <th>Vehicle</th>
                      <th>Source</th>
                      <th>What we found</th>
                      <th>Status</th>
                      <th>Found</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {issues.map((issue) => (
                      <tr key={issue.id}>
                        <td>
                          <div className="vdq-problem">
                            <span>
                              <span className={`admin-badge ${severityClass(issue.severity)}`}>
                                {labelFor(SEVERITY_OPTIONS, issue.severity)}
                              </span>
                            </span>
                            <strong>{issue.issueSummary}</strong>
                            <small>{labelFor(CATEGORY_OPTIONS, issue.issueCategory)}</small>
                          </div>
                        </td>
                        <td>{vehicleLabel(issue)}</td>
                        <td>{issue.sourceName || issue.sourceKey || "—"}</td>
                        <td>{issue.detectedValue || issue.fieldName || "—"}</td>
                        <td>{labelFor(STATUS_OPTIONS, issue.status)}</td>
                        <td>{formatDate(issue.detectedAt)}</td>
                        <td>
                          <RowActionMenu
                            label="Actions"
                            items={[
                              issue.vehicleListingId && onOpenListings
                                ? {
                                    label: "Open listing",
                                    onSelect: () => onOpenListings(issue.vehicleListingId),
                                  }
                                : null,
                              issue.sourceUrl
                                ? {
                                    label: "Open source page",
                                    onSelect: () => window.open(issue.sourceUrl, "_blank"),
                                  }
                                : null,
                              canManage
                                ? {
                                    label: "Start review",
                                    onSelect: () => handlePatch(issue.id, { status: "IN_REVIEW" }),
                                  }
                                : null,
                              canManage
                                ? {
                                    label: "Mark resolved",
                                    onSelect: () => handlePatch(issue.id, { status: "RESOLVED" }),
                                  }
                                : null,
                              canManage
                                ? {
                                    label: "Ignore",
                                    onSelect: () => handlePatch(issue.id, { status: "IGNORED" }),
                                  }
                                : null,
                              canManage
                                ? {
                                    label: "Not a real problem",
                                    onSelect: () =>
                                      handlePatch(issue.id, { status: "FALSE_POSITIVE" }),
                                  }
                                : null,
                              canManage
                                ? {
                                    label: "Needs a parser fix",
                                    onSelect: () =>
                                      handlePatch(issue.id, { status: "NEEDS_PARSER_FIX" }),
                                  }
                                : null,
                              canManage
                                ? {
                                    label: "Needs a name fix",
                                    onSelect: () =>
                                      handlePatch(issue.id, {
                                        status: "NEEDS_NORMALIZATION_FIX",
                                      }),
                                  }
                                : null,
                              canManage
                                ? {
                                    label: "Add a note and resolve",
                                    onSelect: () => setSelectedIssueId(issue.id),
                                  }
                                : null,
                            ].filter(Boolean)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </AdminTableContainer>
            )}

            {selectedIssueId && canManage ? (
              <section className="vdq-panel">
                <h2>Note for this issue</h2>
                <p>The note is saved when you mark the issue resolved.</p>
                <textarea
                  rows={3}
                  value={resolutionNotes}
                  onChange={(event) => setResolutionNotes(event.target.value)}
                  placeholder="What you checked, or why this can be closed."
                />
                <button
                  type="button"
                  className="admin-dashboard__action-btn"
                  onClick={() =>
                    handlePatch(selectedIssueId, {
                      status: "RESOLVED",
                      resolutionNotes: resolutionNotes.trim(),
                    })
                  }
                >
                  Save note and resolve
                </button>
              </section>
            ) : null}
          </section>
        </>
      ) : null}
    </div>
  );
};

export default VehicleDataQualityPage;
